import { Button, Col, Row } from "antd";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import Page from "../../Common/Page";
import LoaderContainer from "../../Common/Page/LoaderContainer";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputNumber, StyledFormSelect } from "../../components/Form/StyledFormInputs";
import { useCreateRule } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import { type rules, rulesList } from "./Rules/ComparisonRules";
import { fields, getTranslatedFields } from "./StatisticFields";

const PermissionName = PermissionNames.LEADER_CRITERIA_SET;

type StatisticField = (typeof fields)[number];
type RulesField = keyof typeof rules;

interface IFormInput {
  statisticField: StatisticField;
  comparisonField: StatisticField;
  comparisonType: RulesField;
  comparisonValue: number;
}

const ManageRules = () => {
  const { sport, organizationId } = useGetOrgData();
  const { leaderCriteriaId, customerId } = useParams();
  const { formatMessage, messages } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { mutate: createFn, isPending: isCreatePending } = useCreateRule();

  useEffect(() => {
    reset({
      statisticField: fields[0].value,
      comparisonField: fields[0].value,
      comparisonType: "EQUAL",
      comparisonValue: 0,
    });
  }, []);

  const title = useMemo(() => {
    return `${formatMessage({
      id: "pageTitles.organization.leader.add.rules",
      defaultMessage: "Add new rule",
    })}`;
  }, [formatMessage]);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    // @ts-ignore
    createFn(data);
  };

  const fields = useMemo(() => {
    return getTranslatedFields({ formatMessage, messages });
  }, [formatMessage, messages]);

  return (
    <Page title={title} tabTitle={title}>
      <LoaderContainer loading={isCreatePending}>
        <>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Col span={12}>
              <Row gutter={16}>
                <Col span={16}>
                  <FormControl
                    labelText="statistic.field"
                    name="statisticField"
                    labelTooltip={formatMessage({
                      id: "form.help.qualification.rule.statistic.field",
                    })}
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "statistic.field",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    render={({ field }) => <StyledFormSelect style={{ width: "100%" }} {...field} options={fields} />}
                    errors={errors}
                  />
                  <FormControl
                    labelText="comparison.field"
                    name="comparisonField"
                    labelTooltip={formatMessage({
                      id: "form.help.qualification.rule.comparison.field",
                    })}
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "comparison.field",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    render={({ field }) => <StyledFormSelect style={{ width: "100%" }} {...field} options={fields} />}
                    errors={errors}
                  />
                  <FormControl
                    labelText="comparison.type"
                    name="comparisonType"
                    labelTooltip={formatMessage({
                      id: "form.help.qualification.rule.comparison.type",
                    })}
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "comparison.field",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    render={({ field }) => (
                      <StyledFormSelect style={{ width: "100%" }} {...field} options={rulesList} />
                    )}
                    errors={errors}
                  />
                  <FormControl
                    labelText="comparison.value"
                    name="comparisonValue"
                    labelTooltip={formatMessage({
                      id: "form.help.qualification.rule.comparison.value",
                    })}
                    rules={{
                      required: formatMessage(
                        {
                          id: "form.help.required.field",
                        },
                        {
                          field: formatMessage({
                            id: "comparison.value",
                          }),
                        },
                      ),
                    }}
                    control={control}
                    render={({ field }) => <StyledFormInputNumber {...field} />}
                  />
                </Col>
              </Row>
              <Row>
                <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
                  <Button htmlType="submit" type="primary">
                    {formatMessage({
                      id: "leaders.rule.create",
                    })}
                  </Button>
                </PermissionCheck>
                <Link
                  to={`/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${leaderCriteriaId}/qualifiers`}
                >
                  <Button style={{ marginLeft: 30 }} type="primary">
                    Close
                  </Button>
                </Link>
              </Row>
            </Col>
          </form>
        </>
      </LoaderContainer>
    </Page>
  );
};

export default ManageRules;
